import { ScrollArea } from '@/components/shadcn/scroll-area'
import { ComponentHeader } from '../shared/ComponentHeader'
import { ComponentTitle } from '../shared/ComponentTitle'
import clsx from 'clsx'
import { ComponentActionsContainer } from '../shared/ComponentActionsContainer'
import { getComponentHeights } from '../utils'
import { ListCardsDataSchema, ListCardsUISchema, ListCard } from './types'
import { useCallback, useMemo, useState } from 'react'
import { SearchInput } from '../shared/SearchInput'
import { isListCardCollapsible, ListCardItem } from './ListCardItem'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { IconV2RotateClockwiseOutline } from '@icons/IconV2RotateClockwiseOutline'
import { IconDownChevronOutline } from '@icons/IconDownChevronOutline'
import { Button } from '@/components/shadcn/button'
import { cn } from '@/lib/utils'
import { SingleSelectCombobox } from '@/components/form-components/SingleSelectCombobox'
import { TableActionsV2 } from '../DataTableV2/components/TableActionsV2'

const NO_STATUS_FILTER_VALUE = '__no_status__'

function buildStatusFilterOptions(cards: ListCard[]) {
  const labels = new Set<string>()
  let hasNoStatus = false

  for (const card of cards) {
    const statusLabel = card.cardData?.status?.label
    if (statusLabel) {
      labels.add(statusLabel)
    } else {
      hasNoStatus = true
    }
  }

  if (labels.size === 0) {
    return []
  }

  const options = Array.from(labels)
    .sort((a, b) => a.localeCompare(b))
    .map((label) => ({ value: label, label }))

  if (hasNoStatus) {
    options.push({ value: NO_STATUS_FILTER_VALUE, label: 'No status' })
  }

  return options
}

function matchesStatusFilter(card: ListCard, selectedStatusFilter: string | null) {
  if (!selectedStatusFilter) return true
  if (selectedStatusFilter === NO_STATUS_FILTER_VALUE) return !card.cardData?.status
  return card.cardData?.status?.label === selectedStatusFilter
}

export function ListCardsContent({
  uiSchema,
  dataSchema,
  tileFilters,
  reloadHandler,
  isRefetching,
}: {
  uiSchema: ListCardsUISchema
  dataSchema: ListCardsDataSchema
  tileFilters?: React.ReactNode
  reloadHandler?: () => void
  isRefetching?: boolean
}) {
  const { title = undefined, description = undefined, emphasizeHeader, canSearch, listCardsActions = [] } = uiSchema
  const hasHeader = !!title && !!tileFilters
  const [search, setSearch] = useState('')
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string | null>(null)
  const [expandedByCardId, setExpandedByCardId] = useState<Record<string, boolean>>({})
  const { contentHeight } = getComponentHeights(emphasizeHeader, hasHeader)

  const statusFilterOptions = useMemo(() => buildStatusFilterOptions(dataSchema.cards), [dataSchema.cards])

  const filteredCards = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()

    return dataSchema.cards.filter((card) => {
      if (!matchesStatusFilter(card, selectedStatusFilter)) return false
      if (!normalizedSearch) return true

      const { title, description } = card.cardUI
      const haystack = [title, description, card.cardData?.status?.label].filter(Boolean).join(' ').toLowerCase()
      return haystack.includes(normalizedSearch)
    })
  }, [dataSchema.cards, search, selectedStatusFilter])

  const collapsibleCards = useMemo(() => filteredCards.filter(isListCardCollapsible), [filteredCards])

  const getIsCardOpen = useCallback(
    (cardId: string, defaultExpanded?: boolean) => expandedByCardId[cardId] ?? defaultExpanded ?? true,
    [expandedByCardId]
  )

  const anyExpanded = collapsibleCards.some((card) => getIsCardOpen(card.cardUI.id, card.cardUI.defaultExpanded))
  const showCollapseAll = anyExpanded

  const handleToggleAllCards = () => {
    const targetOpen = !anyExpanded
    setExpandedByCardId((prev) => {
      const next = { ...prev }
      for (const card of collapsibleCards) {
        next[card.cardUI.id] = targetOpen
      }
      return next
    })
  }

  const handleCardOpenChange = (cardId: string) => (open: boolean) => {
    setExpandedByCardId((prev) => ({ ...prev, [cardId]: open }))
  }

  return (
    <div className="flex h-full flex-col">
      {hasHeader && (
        <ComponentHeader emphasize={emphasizeHeader}>
          <ComponentTitle title={title} description={description} />
          <ComponentActionsContainer>
            <div className="flex items-center gap-1">
              <TableActionsV2 actions={listCardsActions} />
              {collapsibleCards.length > 0 && (
                <TooltipButton tooltip={showCollapseAll ? 'Collapse all' : 'Expand all'}>
                  <Button size="icon" variant="ghost" className="size-8 rounded-full" onClick={handleToggleAllCards}>
                    <IconDownChevronOutline className={cn('transition-transform', showCollapseAll && 'rotate-180')} />
                  </Button>
                </TooltipButton>
              )}
              <TooltipButton tooltip="Refresh data">
                <Button size="icon" variant="ghost" className="size-8 rounded-full" onClick={reloadHandler}>
                  <IconV2RotateClockwiseOutline />
                </Button>
              </TooltipButton>
            </div>
            {statusFilterOptions.length > 1 && (
              <SingleSelectCombobox
                triggerVariant="dropdown"
                options={statusFilterOptions}
                selectedValue={selectedStatusFilter}
                onSelectionChange={setSelectedStatusFilter}
                placeholder="All statuses"
                searchPlaceholder="Search statuses..."
                emptyMessage="No statuses found."
                className="w-44"
              />
            )}
            {canSearch && <SearchInput value={search} onChange={setSearch} placeholder="Search" className="w-56" />}
            {tileFilters}
          </ComponentActionsContainer>
        </ComponentHeader>
      )}
      <ScrollArea className={clsx({ 'pt-5': hasHeader })} style={{ height: contentHeight }}>
        <div className="flex flex-col gap-4">
          {!hasHeader && canSearch && (
            <SearchInput value={search} onChange={setSearch} placeholder="Search" className="w-full max-w-md" />
          )}
          {filteredCards.map((card) => (
            <ListCardItem
              key={card.cardUI.id}
              card={card}
              isOpen={getIsCardOpen(card.cardUI.id, card.cardUI.defaultExpanded)}
              onOpenChange={handleCardOpenChange(card.cardUI.id)}
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  )
}
